import {
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ModalFooter from "@/modules/common/components/ui-kit/modals/modal-footer";
import ModalHeader from "@/modules/common/components/ui-kit/modals/modal-header";
import { FileUploadButton } from "@/modules/common/components/input/file-upload/file-upload";
import { Control, Controller, SubmitHandler, useForm } from "react-hook-form";

import {
  OWNER_ID_TYPE_LABELS,
  OwnerIDType,
} from "@/modules/property-tracks/types";
import useUploadOwnerId from "@/modules/property-tracks/hooks/owner-ids/upload-owner-id.hook";
import { Fragment } from "react/jsx-runtime";
import { TOAST_IDS, ToastType, UploadedFile } from "@/modules/common/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import {
  ACCEPTED_TRACK_FILE_TYPES,
  uploadOwnerIdSchema,
} from "@/modules/property-tracks/utils";
import { showToast } from "@/modules/common/utils";
import { useReadonlyContext } from "@/modules/common/context/read-only.context";
import CommonBottomSheet from "@/modules/common/components/ui-kit/bottom-sheets/common-bottom-sheet";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import CommonModal from "@/modules/common/components/ui-kit/modals/common-modal";

type UploadOwnerIdFormFields = z.infer<ReturnType<typeof uploadOwnerIdSchema>>;

const UploadForm = ({
  handleFiles,
  handleDeleteFile,
  fileError,
  control,
}: {
  handleFiles: (files: File[]) => void;
  handleDeleteFile: (file: UploadedFile) => void;
  fileError: boolean;
  control: Control<UploadOwnerIdFormFields>;
}) => {
  return (
    <form>
      <Box sx={{ width: "100%", height: "100%" }}>
        <Stack gap={(theme) => theme.nawy.spacing.md} sx={{ minWidth: "100%" }}>
          <Stack gap={(theme) => theme.nawy.spacing.xs}>
            <Typography
              sx={(theme) => ({
                ...theme.nawy.typography.english.variants.body.xs.regular,
              })}
            >
              Type
            </Typography>
            <Controller
              name="type"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  size="small"
                  value={field.value ?? ""}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                >
                  {Object.values(OwnerIDType).map((type) => (
                    <MenuItem key={type} value={type}>
                      {OWNER_ID_TYPE_LABELS[type]}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>

          <Stack gap={(theme) => theme.nawy.spacing.xs}>
            <Typography
              sx={(theme) => ({
                ...theme.nawy.typography.english.variants.body.xs.regular,
              })}
            >
              Document
            </Typography>
            <Controller
              name="document"
              control={control}
              render={() => (
                <FileUploadButton
                  setFiles={handleFiles}
                  acceptedFileTypes={ACCEPTED_TRACK_FILE_TYPES["OwnerID"]}
                  maxFiles={10}
                  onDeleteFile={handleDeleteFile}
                />
              )}
            />
            {fileError && (
              <Typography variant="caption" color="error">
                File is required
              </Typography>
            )}
          </Stack>
        </Stack>
      </Box>
    </form>
  );
};

const UploadOwnerId = ({
  customerUid,
  showLabel = false,
}: {
  customerUid?: string;
  showLabel?: boolean;
}) => {
  const { isMobile } = useScreenSize();

  const mutationUploadOwnerId = useUploadOwnerId(customerUid);

  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File[] | null>(null);
  const [fileError, setFileError] = useState<boolean>(false);

  const handleFiles = useCallback((newFiles: File[]) => {
    setFile(newFiles);
  }, []);

  const handleDeleteFile = useCallback((fileToDelete: UploadedFile) => {
    setFile((prevFiles) => {
      if (prevFiles === null) {
        return null;
      }
      const updatedFiles = prevFiles.filter(
        (file) => file.name !== fileToDelete.name,
      );
      return updatedFiles.length > 0 ? updatedFiles : null;
    });
    setFileError(false);
  }, []);

  useEffect(() => {
    if (!file && open) {
      setFileError(true);
    }

    if (file && open) {
      setFileError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const {
    handleSubmit,
    reset,
    control,
  } = useForm<UploadOwnerIdFormFields>({
    resolver: zodResolver(uploadOwnerIdSchema()),
  });

  const resetFormState = () => {
    reset();
    setFileError(false);
    setFile(null);
  };

  const onUpload: SubmitHandler<UploadOwnerIdFormFields> = (values) => {
    if (!file || file.length === 0) {
      setFileError(true);
      return;
    }

    if (!customerUid) return;

    const formData = new FormData();
    formData.append("customerUId", customerUid);
    formData.append("type", values.type);
    file.forEach((f) => {
      formData.append("documents", f);
    });

    mutationUploadOwnerId.mutate(
      { formData },
      {
        onSuccess: () => {
          setOpen(false);
          resetFormState();
          showToast({
            type: ToastType.SUCCESS,
            message: "Owner ID uploaded successfully",
            toastId: TOAST_IDS.UPLOAD_OWNER_ID_SUCCESS,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message || "Error uploading owner ID",
            toastId: TOAST_IDS.UPLOAD_OWNER_ID_ERROR,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
      },
    );
  };

  const { readonly } = useReadonlyContext();

  const handleClose = () => {
    resetFormState();
    setOpen(false);
  };

  return (
    <Fragment>
      <Button
        disabled={readonly || !customerUid}
        variant="text"
        onClick={() => setOpen(true)}
        sx={(t) => ({
          ...t.nawy.typography.english.variants.body.md.semibold,
          color: t.nawy.colors.semantic.text.accent,
          textTransform: "none",
        })}
      >
        {showLabel ? "Upload Owner ID" : "Upload"}
      </Button>
      {isMobile ? (
        <CommonBottomSheet
          isOpen={open}
          onClose={handleClose}
          snapPoints={[0.5, 0.8]}
        >
          <ModalHeader onClose={handleClose} title="Upload Owner ID" />
          <Box overflow="auto" width="100%" height="90%">
            <UploadForm
              handleFiles={handleFiles}
              handleDeleteFile={handleDeleteFile}
              fileError={fileError}
              control={control}
            />
          </Box>
          <Box position="sticky" bottom={0} width="100%">
            <ModalFooter
              onCancel={handleClose}
              cancelButtonText="Cancel"
              confirmButtonText="Upload Owner ID"
              onConfirm={handleSubmit(onUpload)}
            />
          </Box>
        </CommonBottomSheet>
      ) : (
        <CommonModal
          open={open}
          sx={{ width: 750 }}
          onClose={handleClose}
          title="Upload Owner ID"
        >
          <Box sx={{ width: "100%", height: "100%", padding: "16px" }}>
            <UploadForm
              handleFiles={handleFiles}
              handleDeleteFile={handleDeleteFile}
              fileError={fileError}
              control={control}
            />
          </Box>
          <ModalFooter
            onCancel={handleClose}
            cancelButtonText="Cancel"
            confirmButtonText="Upload Owner ID"
            onConfirm={handleSubmit(onUpload)}
            isLoading={mutationUploadOwnerId.isPending}
            isConfirmDisabled={!file}
          />
        </CommonModal>
      )}
    </Fragment>
  );
};

export default UploadOwnerId;
